import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/apiClient";
import axios from "axios"; // Import axios directly

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [viewMode, setViewMode] = useState("grid");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [formationsPerPage] = useState(6);

  const truncateDescription = (text, length = 100) => {
    if (text?.length > length) {
      return text.substring(0, length) + "...";
    }
    return text;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add timeout using axios directly
      const source = axios.CancelToken.source();
      const timeoutId = setTimeout(
        () => source.cancel("Request timeout"),
        10000,
      );

      const [coursesRes, categoriesRes, groupsRes] = await Promise.all([
        apiClient.get("/formations", { cancelToken: source.token }),
        apiClient.get("/categorie_formation", { cancelToken: source.token }),
        apiClient.get("/groupes", { cancelToken: source.token }),
      ]);

      clearTimeout(timeoutId);

      setCourses(Array.isArray(coursesRes.data) ? coursesRes.data : []);
      setCategories(
        Array.isArray(categoriesRes.data) ? categoriesRes.data : [],
      );
      setGroups(Array.isArray(groupsRes.data) ? groupsRes.data : []);
    } catch (err) {
      if (axios.isCancel(err)) {
        // Use axios.isCancel directly
        setError(
          new Error(
            "Délai d'attente dépassé : Veuillez vérifier votre connexion",
          ),
        );
      } else if (err.response) {
        setError(
          new Error(
            `Erreur du serveur : ${err.response.status} - ${
              err.response.data?.message || "Erreur inconnue"
            }`,
          ),
        );
      } else if (err.request) {
        setError(
          new Error(
            "Erreur réseau : Impossible d'atteindre le serveur. Veuillez vérifier votre connexion.",
          ),
        );
      } else {
        setError(new Error(`Erreur : ${err.message}`));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategoryId((prevId) =>
      prevId === categoryId ? null : categoryId,
    );
    setCurrentPage(1);
  };

  const getFormationGroupData = (formationId) => {
    const groupe = groups.find((group) => group.id_formation === formationId);
    return {
      capacite: groupe ? groupe.Capacite : "N/A",
      effectifActuel: groupe ? groupe.Effectif_Actuel : "N/A",
    };
  };

  // Correction du filtrage - utiliser la même logique que dans Home
  const filteredCourses =
    selectedCategoryId === null
      ? courses
      : courses.filter((course) => course.id_cat_for === selectedCategoryId);

  const indexOfLastFormation = currentPage * formationsPerPage;
  const indexOfFirstFormation = indexOfLastFormation - formationsPerPage;
  const currentFormations = filteredCourses.slice(
    indexOfFirstFormation,
    indexOfLastFormation,
  );
  const totalPages = Math.ceil(filteredCourses.length / formationsPerPage);

  const paginate = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo(0, 0);
    }
  };

  const generatePageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <li key={i} className={currentPage === i ? "active" : ""}>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              paginate(i);
            }}
          >
            {i}
          </a>
        </li>,
      );
    }
    return pages;
  };

  // Show full page loading
  if (loading && retryCount === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <div className="text-center">
          <div
            className="spinner-border text-primary"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des cours...</p>
        </div>
      </div>
    );
  }

  // Show full page error
  if (error && courses.length === 0 && categories.length === 0) {
    return (
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="alert alert-danger mt-5" role="alert">
              <div className="text-center">
                <i className="fas fa-exclamation-triangle fa-3x mb-3 text-danger"></i>
                <h4>Erreur de chargement</h4>
                <p className="mb-3">{error.message}</p>
                <button
                  className="btn btn-primary"
                  onClick={handleRetry}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      ></span>{" "}
                      Chargement...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-redo me-2"></i> Réessayer
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderCategories = () => (
    <div className="courses-widget">
      <h4 className="widget-title">Catégories</h4>
      <div className="courses-cat-list">
        <ul className="list-wrap">
          {/* Bouton pour afficher tous les cours */}
          <li>
            <button
              className={`btn btn-sm w-100 text-start ${
                selectedCategoryId === null
                  ? "btn-primary text-white"
                  : "btn-light"
              }`}
              onClick={() => handleCategoryClick(null)}
            >
              Tous les cours ({courses.length})
            </button>
          </li>
          {categories.map((category) => {
            // Correction du comptage - utiliser id_cat_for au lieu de id_cat_formation
            const formationCount = courses.filter(
              (course) => course.id_cat_for === category.id_cat_for,
            ).length;
            const isSelected = selectedCategoryId === category.id_cat_for;
            return (
              <li key={category.id_cat_for}>
                <button
                  className={`btn btn-sm w-100 text-start ${
                    isSelected ? "btn-primary text-white" : "btn-light"
                  }`}
                  onClick={() => handleCategoryClick(category.id_cat_for)}
                >
                  {category.nom_cat_formation} ({formationCount})
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );

  // Fonction pour rendre les cours
  const renderCourses = () => {
    if (currentFormations.length === 0) {
      return (
        <div className="col-12">
          <div className="text-center py-5">
            <h4>Aucun cours trouvé</h4>
            <p>Aucun cours ne correspond à votre sélection.</p>
          </div>
        </div>
      );
    }

    return currentFormations.map((course) => {
      const groupData = getFormationGroupData(course.id_formation);
      return (
        <div key={course.id_formation} className="col-xl-4 col-lg-6 col-md-6">
          <div className="courses__item shine__animate-item">
            <div className="courses__item-thumb">
              <Link
                to={`/course-details/${course.id_formation}`}
                className="shine__animate-link"
              >
                <img
                  src={
                    course.image_formation
                      ? `${process.env.REACT_APP_API_URL}/uploads/${course.image_formation}`
                      : "assets/img/courses/course_thumb01.jpg"
                  }
                  alt={course.nom_formation}
                />
              </Link>
            </div>
            <div className="courses__item-content">
              <ul className="courses__item-meta list-wrap">
                <li className="courses__item-tag">
                  <Link to="#">
                    {categories.find(
                      (cat) => cat.id_cat_for === course.id_cat_for,
                    )?.nom_cat_formation || "Catégorie"}
                  </Link>
                </li>
                <li className="avg-rating">
                  <i className="fas fa-clock"></i> {course.duree_formation}
                </li>
              </ul>
              <h5 className="title">
                <Link to={`/course-details/${course.id_formation}`}>
                  {course.nom_formation}
                </Link>
              </h5>
              <p>{truncateDescription(course.description_formation)}</p>
              <div className="courses__item-bottom">
                <div className="button">
                  <Link to={`/course-details/${course.id_formation}`}>
                    <span className="text">Voir le cours</span>
                    <i className="flaticon-arrow-right"></i>
                  </Link>
                </div>
                <div className="courses__item-bottom-right">
                  <span className="price">{course.prix_formation} GNF</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  // Fonction pour rendre la pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="pagination__wrap">
        <nav aria-label="Page navigation">
          <ul className="list-wrap">
            <li className={currentPage === 1 ? "disabled" : ""}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  paginate(currentPage - 1);
                }}
                aria-label="Précédent"
              >
                <i className="flaticon-arrow-right"></i>
              </a>
            </li>
            {generatePageNumbers()}
            <li className={currentPage === totalPages ? "disabled" : ""}>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  paginate(currentPage + 1);
                }}
                aria-label="Suivant"
              >
                <i className="flaticon-arrow-right"></i>
              </a>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  return (
    <main className="main-area fix">
      {/* Show warning if there was an error but we have some data */}
      {error && (
        <div className="container mt-4">
          <div className="alert alert-warning" role="alert">
            <div className="d-flex justify-content-between align-items-center">
              <small>
                <i className="fas fa-exclamation-triangle me-2"></i> Données
                partielles: {error.message}
              </small>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={handleRetry}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    ></span>{" "}
                    Chargement...
                  </>
                ) : (
                  "Actualiser"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <section
        className="breadcrumb__area breadcrumb__bg"
        data-background="assets/img/bg/breadcrumb_bg1.jpg"
      >
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb__content">
                <h3 className="title">Tous les cours</h3>
                <nav className="breadcrumb">
                  <span>
                    <Link to="/">Accueil</Link>
                  </span>
                  <span className="breadcrumb-separator">
                    <i className="fas fa-angle-right"></i>
                  </span>
                  <span>Cours</span>
                </nav>
              </div>
            </div>
          </div>
        </div>
        <div className="breadcrumb__shape-wrap">
          <img
            src="assets/img/others/breadcrumb_shape01.svg"
            alt=""
            className="alltuchtopdown"
          />
          <img
            src="assets/img/others/breadcrumb_shape02.svg"
            alt=""
            data-aos="fade-right"
            data-aos-delay="300"
          />
          <img
            src="assets/img/others/breadcrumb_shape03.svg"
            alt=""
            data-aos="fade-up"
            data-aos-delay="400"
          />
          <img
            src="assets/img/others/breadcrumb_shape04.svg"
            alt=""
            data-aos="fade-down-left"
            data-aos-delay="400"
          />
          <img
            src="assets/img/others/breadcrumb_shape05.svg"
            alt=""
            data-aos="fade-left"
            data-aos-delay="400"
          />
        </div>
      </section>

      {/* Loading state during retry */}
      {loading && retryCount > 0 && (
        <section className="all-courses-area section-py-120">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12 text-center">
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <span className="ms-3">Mise à jour des cours...</span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {!loading && (
        <section className="all-courses-area section-py-120">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-4 order-2 order-lg-0">
                <aside className="courses__sidebar">{renderCategories()}</aside>
              </div>
              <div className="col-xl-9 col-lg-8">
                <div className="courses-top-wrap">
                  <div className="row align-items-center">
                    <div className="col-md-5">
                      <div className="courses-top-left">
                        <p>
                          {" "}
                          Affichage de {filteredCourses.length} résultats au
                          total{" "}
                        </p>
                      </div>
                    </div>
                    <div className="col-md-7">
                      <div className="d-flex justify-content-center justify-content-md-end align-items-center flex-wrap">
                        <div className="courses-top-right m-0 ms-md-auto">
                          <span className="sort-by">Trier par :</span>
                          <div className="courses-top-right-select">
                            <select name="orderby" className="orderby">
                              <option value="most-popular">
                                {" "}
                                Les plus populaires{" "}
                              </option>
                              <option value="popularity">Popularité</option>
                              <option value="average-rating">
                                {" "}
                                Note moyenne{" "}
                              </option>
                              <option value="latest">Les plus récents</option>
                            </select>
                          </div>
                        </div>
                        <ul
                          className="nav nav-tabs courses__nav-tabs"
                          id="myTab"
                          role="tablist"
                        >
                          <li className="nav-item" role="presentation">
                            <button
                              className={`nav-link ${
                                viewMode === "grid" ? "active" : ""
                              }`}
                              onClick={() => setViewMode("grid")}
                              type="button"
                              role="tab"
                            >
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 18 18"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M6 1H2C1.44772 1 1 1.44772 1 2V6C1 6.55228 1.44772 7 2 7H6C6.55228 7 7 6.55228 7 6V2C7 1.44772 6.55228 1 6 1Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M16 1H12C11.4477 1 11 1.44772 11 2V6C11 6.55228 11.4477 7 12 7H16C16.5523 7 17 6.55228 17 6V2C17 1.44772 16.5523 1 16 1Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M6 11H2C1.44772 11 1 11.4477 1 12V16C1 16.5523 1.44772 17 2 17H6C6.55228 17 7 16.5523 7 16V12C7 11.4477 6.55228 11 6 11Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M16 11H12C11.4477 11 11 11.4477 11 12V16C11 16.5523 11.4477 17 12 17H16C16.5523 17 17 16.5523 17 16V12C17 11.4477 16.5523 11 16 11Z"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section d'affichage des cours */}
                <div
                  className="tab-content courses__tab-content"
                  id="myTabContent"
                >
                  <div className="tab-pane fade show active" role="tabpanel">
                    <div className="row courses__grid-wrap">
                      {renderCourses()}
                    </div>
                    {/* Pagination */}
                    {renderPagination()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default Courses;
